import React from 'react';
import { Paper, useMediaQuery, useTheme } from '@material-ui/core';
import { ReportCardSchema } from './schema-types';
import { SchemaRenderer } from './schema-renderer';
import { reportCardLevelService } from '../../../lib/services/report-card-level.service';
import iceSurface from '../../../assets/ice-surface.jpg';

// Schema-driven report card component

interface SchemaDrivenReportCardProps {
  schema: ReportCardSchema;
  player: IPlayer;
  playerAssessment: IPlayerAssessment;
  data?: any;
  organizationLogo?: string | React.ReactNode;
  organizationLogoHeight?: string | number;
  customStyles?: { [key: string]: any };
}

export const SchemaDrivenReportCard: React.FC<SchemaDrivenReportCardProps> = ({
  schema,
  player,
  playerAssessment,
  data,
  organizationLogo,
  organizationLogoHeight,
  customStyles = {}
}) => {
  // Get report card level from player assessment data (fetched from server)
  const reportCardLevel = reportCardLevelService.getReportCardLevel(playerAssessment);

  // Responsive breakpoints
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Responsive styling that maintains aspect ratios
  const getResponsiveStyles = () => {
    const baseStyles = {
      position: 'relative' as const,
      borderRadius: isMobile ? 8 : 16,
      backgroundImage: `url(${iceSurface})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat' as const,
      backgroundColor: 'rgba(255,255,255,0.8)',
      backgroundBlendMode: 'lighten' as const,
    };

    if (isMobile) {
      return {
        ...baseStyles,
        // Mobile: Full width with responsive padding
        width: '100%',
        maxWidth: '100%',
        margin: '0',
        padding: '8px 6px', // Half of the original padding
        // Maintain proportional scaling
        minHeight: 'auto',
      };
    } else if (isTablet) {
      return {
        ...baseStyles,
        // Tablet: Scaled down proportionally
        maxWidth: 600,
        margin: '24px auto',
        padding: 24,
      };
    } else {
      return {
        ...baseStyles,
        // Desktop: Original sizing
        maxWidth: 700,
        margin: '32px auto',
        padding: 32,
      };
    }
  };

  const defaultStyles = {
    ...getResponsiveStyles(),
    ...schema.styling?.customStyles,
    ...customStyles
  };

  // Build context object for data binding with responsive info
  const context = {
    schema,
    reportCardLevel,
    // Add responsive context for components to use
    responsive: {
      isMobile,
      isTablet,
      isDesktop: !isMobile && !isTablet,
      breakpoint: isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop'
    },
    ...data
  };

  return (
    <Paper
      className="print-report-card-paper"
      elevation={3}
      style={defaultStyles}
    >
      {/* Render layout using schema */}
      {schema.layout && (
        <SchemaRenderer
          schema={schema.layout}
          data={data}
          player={player}
          playerAssessment={playerAssessment}
          reportCardLevel={reportCardLevel}
          organizationLogo={organizationLogo}
          organizationLogoHeight={organizationLogoHeight}
          context={context}
        />
      )}

      {/* Render individual components if no layout specified */}
      {!schema.layout && schema.components?.map((componentSchema, index) => (
        <SchemaRenderer
          key={index}
          schema={componentSchema}
          data={data}
          player={player}
          playerAssessment={playerAssessment}
          reportCardLevel={reportCardLevel}
          organizationLogo={organizationLogo}
          organizationLogoHeight={organizationLogoHeight}
          context={context}
        />
      ))}
    </Paper>
  );
};
